import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataCell,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  SuspenseLoader,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@folio/components';
import { cn, formatDate } from '@folio/lib';
import { isLineVisibleForRole, useFirmType } from '@folio/hooks';
import {
  getDefaultLines,
  getTotals,
  getQuoteLineTranslationKeys,
  getTooltipKeys,
  getVariantByKey,
  getVisibleLines,
} from '@folio/utils/price-display-utils';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useQuery } from '@tanstack/react-query';
import {
  branchControllerGetBranchEffectiveDateQueryOptions,
  pricingControllerGetPriceQueryOptions,
  type PriceBreakdownLineOutputDto,
  type DisplayLineSubLineOutputDto,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { GitCompareArrows, InfoIcon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumCompareModule } from '../PremiumCompareModule/PremiumCompareModule';

export function PremiumModule() {
  const { t } = useTranslation();

  const { firmType } = useFirmType();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policy.productId];
  const priceBreakdownConfig = productConfig?.priceBreakdownConfig;

  const {
    data: price,
    isLoading,
    isError,
  } = useQuery({
    ...pricingControllerGetPriceQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
  });

  const isAmendment =
    workingBranch?.branchType === 'MTA' || workingBranch?.branchType === 'RENEWAL';

  const { data: branchEffectiveDate } = useQuery({
    ...branchControllerGetBranchEffectiveDateQueryOptions(policy.id, workingBranch?.id ?? ''),
    enabled: isAmendment && !!workingBranch?.id,
  });

  if (isError) return null;

  const hasDisplayLines = (price?.lines?.length ?? 0) > 0 || !!price?.totalLine;
  const apiLines = hasDisplayLines ? price?.lines : price ? getDefaultLines(price) : undefined;

  const variantByKey = getVariantByKey(priceBreakdownConfig);
  const tooltipKeys = getTooltipKeys(priceBreakdownConfig);

  const visibleLines = getVisibleLines({
    lines: apiLines,
    price,
    priceBreakdownConfig,
    firmType,
  });

  const totals = price ? getTotals(price, priceBreakdownConfig) : [];

  if (!isLoading && visibleLines.length === 0 && totals.length === 0) return null;

  const baseTitle = t([`PremiumCard.title_${price?.frequency}`, 'PremiumCard.title']);
  const titleSuffix = !isAmendment
    ? ''
    : branchEffectiveDate?.effectiveAt
      ? t('PremiumCard.titleSuffixFromDate', {
          date: formatDate(new Date(branchEffectiveDate.effectiveAt)),
        })
      : t('PremiumCard.titleSuffixFromSignature');

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{titleSuffix ? `${baseTitle} ${titleSuffix}` : baseTitle}</CardTitle>
        {isAmendment && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" leftIcon={<GitCompareArrows className="h-4 w-4" />}>
                {t('PremiumCard.compareWithCurrent')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="sr-only">{t('PremiumCard.compareWithCurrent')}</DialogTitle>
              </DialogHeader>
              <DialogDescription className="sr-only">
                {t('PremiumCard.compareWithCurrent')}
              </DialogDescription>
              <SuspenseLoader>
                <PremiumCompareModule branches={[undefined, workingBranch?.id]} />
              </SuspenseLoader>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {priceBreakdownConfig?.description && <div>{priceBreakdownConfig.description}</div>}
        <table>
          <tbody>
            {visibleLines.map((line: PriceBreakdownLineOutputDto) => {
              const variant = variantByKey.get(line.key);
              return (
                <React.Fragment key={line.key}>
                  <tr>
                    <td
                      className={cn(
                        'pr-12 text-sm',
                        variant === 'dimmed' ? 'text-gray-600' : 'font-semibold',
                      )}
                    >
                      <span className="inline-flex items-center">
                        {t(getQuoteLineTranslationKeys(policy.productId, line.key))}
                        {tooltipKeys.has(line.key) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <InfoIcon className="ml-2 h-4 w-4 text-muted-foreground cursor-help font-normal" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-sm">
                                {t(
                                  getQuoteLineTranslationKeys(
                                    policy.productId,
                                    `${line.key}_tooltip`,
                                  ),
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    </td>
                    <DataCell
                      type={line.type || 'price'}
                      className={cn(
                        'p-2 text-right w-2/5',
                        variant !== 'dimmed' && 'font-semibold',
                      )}
                      isLoading={isLoading}
                      value={line.adjustedAmount ?? line.amount}
                    />
                  </tr>
                  {line.subLines
                    ?.filter((subLine: DisplayLineSubLineOutputDto) =>
                      isLineVisibleForRole(subLine, firmType),
                    )
                    .map((subLine: DisplayLineSubLineOutputDto) => (
                      <tr key={subLine.key}>
                        <td className="pr-12 text-sm text-gray-600">
                          {t(getQuoteLineTranslationKeys(policy.productId, subLine.key))}
                        </td>
                        <DataCell
                          type={subLine.type || 'price'}
                          className="p-2 text-right w-2/5"
                          isLoading={isLoading}
                          value={subLine.adjustedAmount ?? subLine.amount}
                        />
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}

            {totals.map(totalLine => (
              <tr key={totalLine.key} className="font-semibold">
                <td className="pr-12 text-secondary text-sm">
                  {t(getQuoteLineTranslationKeys(policy.productId, totalLine.key))}
                </td>
                <DataCell
                  type="price"
                  className="p-2 text-right w-2/5 text-secondary"
                  isLoading={isLoading}
                  value={totalLine.amount}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
